import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';

import { HttpClient, HttpHandler } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError, forkJoin } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { AppConfig } from '@/app.config';
import {
    User,
    CommonList,
    ObjectiveTag,
    CommonData,
    Skill,
    Country,
    Role,
    Language
} from '@/auth/models';
import { ContentService } from '@/pages/content/content.service';
import { DashboardService } from '@/pages/dashboard/dashboard.service';

export class Tokens {
    jwt: string;
    refreshToken: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    public readonly currentUserSubject: BehaviorSubject<User>;
    private commonLists: CommonList = {} as CommonList;
    public dataInitialized$: BehaviorSubject<string> = new BehaviorSubject('PENDING');

    constructor(
        private http: HttpClient,
        private httpHandler: HttpHandler,
        private contentService: ContentService,
        private dashboardService: DashboardService,
        private config: AppConfig,
        private logger: NGXLogger,
        private router: Router
    ) {
        this.httpHandler = httpHandler; // If we want to create a httpClient to skip interceptions
        this.currentUserSubject = new BehaviorSubject<User>(
            JSON.parse(localStorage.getItem('currentUser'))
        );
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    public get token(): string | null {
        return this.currentUserValue ? this.currentUserValue.token : null;
    }

    login(username: string, password: string) {
        this.logger.debug('Authenticating User: ' + username);
        // NOTE: All URLs are appended by slash (APPEND_SLASH is set in Django Settings!) // TODO: Can be done in intercept!
        return this.http
            .post<any>(`${this.config.apiUrl}/auth/login/`, { username, password })
            .pipe(
                map(response => {
                    // login successful if there's a jwt token in the response
                    if (response && response.access) {
                        const user = this.setUserData(response);
                        // store user details and jwt token in local storage to keep user logged in between page refreshes
                        localStorage.setItem('currentUser', JSON.stringify(user));
                        localStorage.setItem('wf_role', response.user.role);
                        localStorage.setItem('wf_org', JSON.stringify(response.user.org_id));
                        localStorage.setItem('wf_user_hash', response.user.user_hash);
                        this.currentUserSubject.next(user);
                        this.dataInitialized$.next('PENDING');
                        this.initializeData();
                    }
                    return true;
                })
            );
    }

    register(user: User) {
        return this.http.post(`${this.config.apiUrl}/auth/register/`, user);
    }

    refreshToken(user: User): Observable<string> {
        return this.http
            .post<any>(`${this.config.apiUrl}/token/refresh/`, { refresh: user.refreshToken })
            .pipe(
                tap((response: any) => {
                    user.token = response.access;
                    localStorage.setItem('currentUser', JSON.stringify(user));
                    this.currentUserSubject.next(user);
                    this.logger.trace('Refreshed Token for: ', user);
                }),
                map((response: any) => {
                    this.logger.trace('Refresh Token Map: ', response);
                    return response.access;
                })
            );
    }

    logout() {
        // We need the auth token while calling the logout API. So, we set the user.loggedIn to false
        const user = this.currentUserValue;
        if (user) {
            user.loggedIn = false;
        }
        this.currentUserSubject.next(user);
        this.http
            .post<any>(`${this.config.apiUrl}/auth/logout/`, {})
            .pipe(
                map(_ => true),
                catchError(_error => of(false)) // Continue!
            )
            .subscribe(_ => {
                // clear localStorage and sessionStorage to completely log user out
                sessionStorage.clear();
                localStorage.clear();
                this.contentService.clear();
                this.dashboardService.clear();
                this.clear();
                this.currentUserSubject.next(null);
                this.router.navigate(['login']);
            });
        return of(true); // Return an observable.
    }

    private setUserData(response): User {
        const user = new User();
        user.token = response.access;
        user.refreshToken = response.refresh;
        user.groups = response.groups;
        user.views = response.views;
        user.pageActions = response.actions;
        user.readOnly = response.read_only || false;
        user.orgId = response.user.org_id;
        user.userHash = response.user.user_hash;
        user.hideOtp = response.hide_otp || false;
        // this.logger.trace('Navigation Groups:' + JSON.stringify(user.groups));

        // user.views['manage_accounts'] = {
        //     ...user.views['manage_accounts'],
        //     url: '/journey_plan/manage_accounts/'
        // };
        // for dev purpose
        // const incentiveGroup = user.groups.find(group => group.name === 'Incentives');
        // if (incentiveGroup) {
        //     incentiveGroup.views.push('code_management');
        //     user.views['code_management'] = {
        //         name: 'Code Management',
        //         icon: 'fa-code',
        //         url: '/incentive/code_management/'
        //     };
        // }

        // decode the token to read the user details and expiration timestamp
        const tokenParts = response.access.split(/\./);
        const tokenDecoded = JSON.parse(window.atob(tokenParts[1]));

        user.tokenExpiry = new Date(tokenDecoded.exp * 1000);
        // this.logger.debug('Decoded Token: ', tokenDecoded);
        user.email = tokenDecoded.admin_name;
        user.firstName = tokenDecoded.first_name;
        user.lastName = tokenDecoded.last_name;
        user.id = tokenDecoded.admin_id;
        user.loggedIn = true;
        return user;
    }

    changePwd(formdata) {
        return this.http.post<any>(`${this.config.apiUrl}/auth/reset_password/`, formdata);
    }
    forgotPwd(email) {
        return this.http.post<any>(`${this.config.apiUrl}/auth/send_reset_paswd/`, {
            email_id: email
        });
    }
    validateToken(token) {
        return this.http.post<any>(`${this.config.apiUrl}/auth/validate_token/`, {
            token
        });
    }
    resetPwd(formdata) {
        return this.http.post<any>(`${this.config.apiUrl}/auth/change_password/`, formdata);
    }

    getUserPermission() {
        return JSON.parse(localStorage.getItem('currentUser')).readOnly;
    }

    // Common API calls....
    clear() {
        this.commonLists = {} as CommonList;
    }

    clearSkills() {
        // Clear Skills, Objectives when users save cats/skills!
        sessionStorage.removeItem('skills');
        sessionStorage.removeItem('skillObjectives');
        this.dataInitialized$.next('PENDING');
    }

    // APIs specific to Common Data (Country, Role, Agency, BU)
    initializeData() {
        if (['INITIALIZED', 'IN_PROGRESS'].includes(this.dataInitialized$.value)) {
            // console.log('Current Initialization Status = ', this.dataInitialized$.value, 'Returning');
            return this.dataInitialized$;
        }
        const countryRoleData$ = this.apiCountryWithAgency();
        const skillObjectiveData$ = this.apiObjectivesData();
        const langData$ = this.apiLanguageList();
        this.dataInitialized$.next('IN_PROGRESS');
        forkJoin([countryRoleData$, skillObjectiveData$, langData$]).subscribe({
            next: _resultArray => {
                // console.log('Loaded Country, Role & Skill Objectives Data');
                return this.dataInitialized$.next('INITIALIZED');
            },
            error: error => {
                // console.log('Error Encountered While Loading Country Role Data & Skill Objective Data: ' + error.message);
                this.dataInitialized$.next('ERROR');
                throwError(() => error);
            }
        });
        return this.dataInitialized$;
    }

    roleList(includeInactive = false): Role[] {
        return (
            includeInactive
                ? this.commonLists.role.concat(this.commonLists.inactiveRoles)
                : this.commonLists.role
        ) as Role[];
    }

    countryList(excludeData = false): Country[] {
        if (excludeData) {
            const activeCountries = this.commonLists.activeCountries;
            const availableCountries = (
                activeCountries.length
                    ? this.commonLists.countryAgency.filter(
                          (c: Country) => activeCountries.indexOf(c.value) > -1
                      )
                    : this.commonLists.countryAgency
            ) as Country[];
            // Select only countries with roles.
            return availableCountries.filter((item: Country) => {
                return item.roles.length;
            });
        } else {
            const countryList = this.commonLists.countryAgency as Country[];
            return countryList.filter((item: Country) => {
                return item.roles.length;
            });
        }
    }

    skills(): Skill[] {
        return this.commonLists.skills.sort((a: Skill, b: Skill) => {
            return a.tagName === b.tagName ? 0 : a.tagName > b.tagName ? 1 : -1;
        }) as Skill[];
    }

    skillList(): Skill[] {
        return this.commonLists.skillObjectives.sort((a: Skill, b: Skill) => {
            return a.tagName === b.tagName ? 0 : a.tagName > b.tagName ? 1 : -1;
        }) as Skill[];
    }

    skillListMap() {
        return this.commonLists.skillObjectives.reduce((acc, curr: Skill) => {
            return (acc[curr.tagId] = curr.tagName), acc;
        }, {});
    }

    language(): Language[] {
        return this.commonLists.language.sort((a: Language, b: Language) => {
            return a.label === b.label ? 0 : a.label > b.label ? 1 : -1;
        }) as Language[];
    }

    languageMap() {
        return this.commonLists.language.reduce((acc, curr: Language) => {
            return (acc[curr.value] = curr.label), acc;
        }, {});
    }

    apiObjectivesData() {
        const sessionObservable = this.getSessionObservable(['skills', 'skillObjectives']);
        if (sessionObservable) {
            return sessionObservable;
        }
        // Else Call the API to get the HTTP Observable!
        const objectiveObservable = this.http
            .get<any>(`${this.config.apiUrl}/section/get_objectives_for_sections/`, {})
            .pipe(
                map(response => {
                    const skills = response.map(v => {
                        const objectives: ObjectiveTag = v.objectives.map(vo => {
                            return {
                                tagId: vo.tag_id,
                                tagName: vo.tag_name,
                                hasModule: vo.has_module
                            };
                        });
                        return { tagId: v.tag_id, tagName: v.tag_name, objectives };
                    });
                    this.setCommonData('skills', skills as Skill[]);

                    const skillObjectives = [];
                    response.forEach(element => {
                        const skill = {
                            tagId: element.tag_id,
                            tagName: element.tag_name
                        };

                        skillObjectives.push(skill);

                        element.objectives.forEach(n => {
                            const objective = {
                                tagId: n.tag_id,
                                tagName: element.tag_name + ' | ' + n.tag_name
                            };

                            skillObjectives.push(objective);
                        });
                    });
                    this.setCommonData('skillObjectives', skillObjectives);
                    return { skills, skillObjectives };
                })
            );
        return objectiveObservable;
    }

    apiCountryWithAgency() {
        const sessionObservable = this.getSessionObservable([
            'countryAgency',
            'role',
            'inactiveRoles',
            'activeCountries'
        ]);
        if (sessionObservable) {
            return sessionObservable;
        }
        // Else Call the API to get the HTTP Observable!
        const roleObservable = this.http
            .post<any>(`${this.config.apiUrl}/teams/get_teams_details/`, {
                incentive_id: null,
                category_id: null
            })
            .pipe(
                map(response => {
                    const countryAgencyList = response.countries.sort((a, b) => {
                        return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
                    });
                    this.setCommonData('countryAgency', countryAgencyList as Country[]);
                    this.setCommonData('role', response.role as Role[]);
                    this.setCommonData('inactiveRoles', (response.inactiveRoles || []) as Role[]);
                    this.setCommonData(
                        'activeCountries',
                        (response.availableCountries || []) as string[]
                    );
                    return {
                        role: response.role,
                        countryAgency: countryAgencyList,
                        inactiveRoles: response.inactiveRoles,
                        activeCountries: response.activeCountries
                    };
                })
            );
        return roleObservable;
    }

    apiLanguageList() {
        const sessionObservable = this.getSessionObservable(['language']);
        if (sessionObservable) {
            return sessionObservable;
        }
        // Else Call the API to get the HTTP Observable!
        return this.http.get<any>(`${this.config.apiUrl}/language/get_langlist/`, {}).pipe(
            map(response => {
                const globalLangList: Language[] = response.admin_langs.sort((a, b) => {
                    if (a.value === 'EN_GLOBAL') {
                        return -1;
                    }
                    if (b.value === 'EN_GLOBAL') {
                        return 1;
                    }
                    return a.label.toLowerCase().replace('(', ' ').replace(')', ' ') >
                        b.label.toLowerCase().replace('(', ' ').replace(')', ' ')
                        ? 1
                        : -1;
                });
                const en_global_found = globalLangList.find(el => el.value == 'EN_GLOBAL');
                if (!en_global_found) {
                    globalLangList.push({
                        label: 'English Global',
                        value: 'EN_GLOBAL',
                        translationEnabled: true
                    });
                }
                this.setCommonData('language', globalLangList as Language[]);
                return { language: globalLangList };
            })
        );
    }

    getSessionObservable(keys: Array<string>) {
        const found = keys.every(type => {
            if (sessionStorage.getItem(type)) {
                this.commonLists[type] = JSON.parse(sessionStorage.getItem(type));
                return true;
            }
            return false;
        });
        if (found) {
            const returnValue = {};
            keys.forEach(key => (returnValue[key] = this.commonLists[key]));
            return of(returnValue);
        }
        return null;
    }

    setCommonData(type, value: CommonData[]) {
        this.commonLists[type] = value;
        sessionStorage.setItem(type, JSON.stringify(value));
    }

    getRoleAgencyList(countryData) {
        const apiRoleList = this.roleList(true); // Include Inactive Roles
        const roleList = [...apiRoleList];
        let roleAgencyList = [];
        countryData.forEach((country, _index) => {
            const countryRoleList = roleList.filter(item => {
                return country.roles.indexOf(item.value) > -1;
            });

            countryRoleList.forEach(item => {
                const countryBunit = country.business_unit;
                const countryAgency = country.details;

                // code for role- bunit combination...
                if (countryBunit.length) {
                    countryBunit.forEach((bunit, i) => {
                        roleAgencyList.push({
                            label: item.label + '-' + bunit,
                            value: { role_id: item.value, agency_id: null, bunit },
                            role: item.label,
                            roleId: item.value,
                            id: uuid()
                        });
                    });
                }

                if (Object.keys(countryAgency).length) {
                    Object.keys(countryAgency).forEach(agency => {
                        // code for role - agency...
                        roleAgencyList.push({
                            label: item.label + '-' + countryAgency[agency].name,
                            value: {
                                role_id: item.value,
                                agency_id: parseInt(agency, 10),
                                bunit: null
                            },
                            role: item.label,
                            roleId: item.value,
                            id: uuid()
                        });

                        // code for role-agency-bunit...
                        const agencyBunit = countryAgency[agency].business_unit;
                        if (agencyBunit.length) {
                            agencyBunit.forEach((bunit, i) => {
                                roleAgencyList.push({
                                    label:
                                        item.label + '-' + countryAgency[agency].name + '-' + bunit,
                                    value: {
                                        role_id: item.value,
                                        agency_id: parseInt(agency, 10),
                                        bunit
                                    },
                                    role: item.label,
                                    roleId: item.value,
                                    id: uuid()
                                });
                            });
                        }
                    });
                }
            });
            roleAgencyList = roleAgencyList.filter(
                (v, i, a) => a.findIndex(t => t.value === v.value) === i
            );
            roleAgencyList.sort((a, b) => {
                return a.label === b.label ? 0 : a.label > b.label ? 1 : -1;
            });
        });

        return roleAgencyList;
    }
}
